import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import './App.css';

const SERVICE_ID = 'service_uqls2bf';
const TEMPLATE_ID = 'template_mrow3f6';
const PUBLIC_KEY = 'S8k2oZiI7iaPC8lRM';

function App() {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', service: '', message: '' });
  const [status, setStatus] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setStatus('sending');

    const templateParams = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      service: formData.service,
      message: formData.message,
      time: new Date().toLocaleString(),
    };

    emailjs.send(SERVICE_ID, TEMPLATE_ID, templateParams, PUBLIC_KEY)
      .then(() => {
        setStatus('success');
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      })
      .catch(() => {
        setStatus('error');
      });
  };

  return (
    <div className="App">
      {/* Header/Navigation */}
      <header className="header">
        <nav className="navbar">
          <div className="logo">
            <h1>Malach <span>Healthcare</span></h1>
          </div>
          <ul className="nav-links">
            <li><a href="#home">Home</a></li>
            <li><a href="#about">About Us</a></li>
            <li><a href="#services">Services</a></li>
            <li><a href="#contact">Contact</a></li>
          </ul>
          <a href="#contact" className="btn btn-primary">Book Consultation</a>
        </nav>
      </header>

      {/* Hero Section */}
      <section id="home" className="hero">
        <div className="hero-content">
          <h1>Elevating lives through heartfelt care.</h1>
          <p> Malach Healthcare is the heart behind every smile through empowerment and compassionate care,</p>
          <div className="hero-buttons">
            <a href="#contact" className="btn btn-primary">Book A Free Consultation</a>
            <a href="#about" className="btn btn-secondary">What We Do</a>
          </div>
        </div>
        <div className="hero-image">
          <div className="hero-image-placeholder">
            <span>👨‍⚕️</span>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="mission-vision">
        <div className="mission-vision-container">
          <div className="mission-card">
            <div className="card-icon">🎯</div>
            <h3>Mission</h3>
            <p>Collaborating with individuals, families, and communities, we craft innovative solutions for life's most significant challenges. We begin by actively listening, and then, united, we take decisive action to empower thriving.</p>
          </div>
          <div className="vision-card">
            <div className="card-icon">👁️</div>
            <h3>Vision</h3>
            <p>To be the cornerstone of support for individuals, families, and communities facing unique senior care challenges. With unwavering dedication, we aspire to lead in redefining holistic well-being and enriching lives on every step of the journey.</p>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="services">
        <div className="services-header">
          <h2>Our Services</h2>
          <p>Comprehensive care tailored to your loved ones' needs</p>
        </div>
        <div className="services-grid">
          <div className="service-card">
            <div className="service-icon">🛁</div>
            <h3>Personal Care</h3>
            <p>From dignified hygiene to steady walks, our services ensure cherished comfort for the elderly.</p>
            <Link to="/personal-care" className="learn-more">Learn More →</Link>
          </div>
          <div className="service-card">
            <div className="service-icon">💝</div>
            <h3>Companion Care</h3>
            <p>Nourishing both body and spirit, we provide more than just meals – our companionship fills hearts with warmth and laughter.</p>
            <Link to="/companion-care" className="learn-more">Learn More →</Link>
          </div>
          <div className="service-card">
            <div className="service-icon">🧠</div>
            <h3>Dementia Care</h3>
            <p>Providing invaluable assistance to individuals living with dementia and their families, offering compassionate guidance and unwavering support.</p>
            <Link to="/dementia-care" className="learn-more">Learn More →</Link>
          </div>
          
          <div className="service-card">
            <div className="service-icon">⭐</div>
            <h3>Specialty Care</h3>
            <p>Caring for Diverse Needs: Supporting Veterans, Disabilities, Recovery and more.</p>
            <Link to="/specialty-care" className="learn-more">Learn More →</Link>
          </div>
          <div className="service-card">
            <div className="service-icon">🩺</div>
            <h3>Therapists</h3>
            <p>Providing comprehensive therapy care services for individuals seeking holistic well-being.</p>
            <Link to="/therapeutic-care" className="learn-more">Learn More →</Link>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about">
        <div className="about-content">
          <div className="about-image">
            <div className="about-image-placeholder">
              <span>👴👵</span>
            </div>
          </div>
          <div className="about-text">
            <h2>Elevating elders' lives through heartfelt care.</h2>
            <p>We are more than a healthcare company – we are a passionate advocate for seniors, delivering tailored and compassionate care that enhances their well-being and celebrates this vibrant stage of life.</p>
            <ul className="about-features">
              <li>✓ 24/7 Professional Care</li>
              <li>✓ Experienced & Certified Caregivers</li>
              <li>✓ Personalized Care Plans</li>
              <li>✓ Family-Centered Approach</li>
            </ul>
            <a href="#contact" className="btn btn-primary">Get Started Today</a>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats">
        <div className="stats-container">
          <div className="stat-item">
            <h3>500+</h3>
            <p>Families Served</p>
          </div>
          <div className="stat-item">
            <h3>50+</h3>
            <p>Certified Caregivers</p>
          </div>
          <div className="stat-item">
            <h3>10+</h3>
            <p>Years Experience</p>
          </div>
          <div className="stat-item">
            <h3>98%</h3>
            <p>Client Satisfaction</p>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="testimonials">
        <h2>What Families Say About Us</h2>
        <div className="testimonials-grid">
          <div className="testimonial-card">
            <p>"Malach Healthcare has been a blessing for our family. The caregivers are professional, compassionate, and truly care about my mother's well-being."</p>
            <div className="testimonial-author">
              <span className="author-avatar">👤</span>
              <div>
                <strong>Sarah Johnson</strong>
                <p>Daughter of Client</p>
              </div>
            </div>
          </div>
          <div className="testimonial-card">
            <p>"The personal care services have been wonderful. The caregivers are professional, reliable, and truly care about my mother's comfort and dignity."</p>
            <div className="testimonial-author">
              <span className="author-avatar">👤</span>
              <div>
                <strong>Michael Williams</strong>
                <p>Son of Client</p>
              </div>
            </div>
          </div>
          <div className="testimonial-card">
            <p>"Outstanding dementia care. The staff is well-trained and shows genuine patience and love. My husband's quality of life has improved significantly."</p>
            <div className="testimonial-author">
              <span className="author-avatar">👤</span>
              <div>
                <strong>Patricia Davis</strong>
                <p>Wife of Client</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact">
        <div className="contact-container">
          <div className="contact-info">
            <h2>Get In Touch</h2>
            <p>Ready to provide the best care for your loved ones? Contact us today for a free consultation.</p>
            <div className="contact-details">
              <div className="contact-item">
                <span>📍</span>
                <p>740 Main St<br />Waltham, MA 02451</p>
              </div>
              <div className="contact-item">
                <span>📞</span>
                <p>(781) 697-6330</p>
              </div>
              <div className="contact-item">
                <span>✉️</span>
                <p>info@malachhealthcare.com</p>
              </div>
              <div className="contact-item">
                <span>🕐</span>
                <p>Mon - Fri: 8:00 AM - 6:00 PM<br />24/7 Emergency Care Available</p>
              </div>
            </div>
          </div>
          <div className="contact-form">
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <input type="text" name="name" placeholder="Your Name" value={formData.name} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <input type="email" name="email" placeholder="Your Email" value={formData.email} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <input type="tel" name="phone" placeholder="Phone Number" value={formData.phone} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <select name="service" value={formData.service} onChange={handleChange}>
                  <option value="">Select Service</option>
                  <option value="Personal Care">Personal Care</option>
                  <option value="Companion Care">Companion Care</option>
                  <option value="Dementia Care">Dementia Care</option>
                  <option value="Specialty Care">Specialty Care</option>
                  <option value="Therapy Services">Therapy Services</option>
                </select>
              </div>
              <div className="form-group">
                <textarea name="message" placeholder="Tell us about your care needs..." rows="4" value={formData.message} onChange={handleChange}></textarea>
              </div>
              <button type="submit" className="btn btn-primary btn-full" disabled={status === 'sending'}>
                {status === 'sending' ? 'Sending...' : 'Request Free Consultation'}
              </button>
              {status === 'success' && (
                <p style={{ color: 'green', marginTop: '10px', textAlign: 'center' }}>
                  Thank you! We'll be in touch soon.
                </p>
              )}
              {status === 'error' && (
                <p style={{ color: 'red', marginTop: '10px', textAlign: 'center' }}>
                  Something went wrong. Please call us at (781) 697-6330.
                </p>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-section">
            <h3>Malach <span>Healthcare</span></h3>
            <p>Empowering golden years with compassionate care.</p>
            <div className="social-links">
              <a href="#facebook">📘</a>
              <a href="#twitter">🐦</a>
              <a href="#instagram">📷</a>
              <a href="#linkedin">💼</a>
            </div>
          </div>
          <div className="footer-section">
            <h4>Quick Links</h4>
            <ul>
              <li><a href="#home">Home</a></li>
              <li><a href="#about">About Us</a></li>
              <li><a href="#services">Services</a></li>
              <li><a href="#contact">Contact</a></li>
            </ul>
          </div>
          <div className="footer-section">
            <h4>Our Services</h4>
            <ul>
              <li><Link to="/personal-care">Personal Care</Link></li>
              <li><Link to="/companion-care">Companion Care</Link></li>
              <li><Link to="/dementia-care">Dementia Care</Link></li>
              <li><Link to="/specialty-care">Specialty Care</Link></li>
            </ul>
          </div>
          <div className="footer-section">
            <h4>Contact Info</h4>
            <p>📍 740 Main St<br />Waltham, MA 02451</p>
            <p>📞 (781) 697-6330</p>
            <p>✉️ info@malachhealthcare.com</p>
          </div>
        </div>
        <div className="footer-bottom">
          <p>Copyright © 2025 | All Rights Reserved | Malach Healthcare</p>
        </div>
      </footer>
    </div>
  );
}

export default App;
